// 大学搜索工具函数
// 支持别名搜索和首字母联想

import { getUniversityAliases } from '@/data/universityAliases'
import type { University } from '@/types'

// 去除变音符号（德语特殊字符）
const stripDiacritics = (s: string): string => 
  s.normalize('NFD').replace(/[\u0300-\u036f]/g, '')

// 标准化搜索文本：去除变音符号、转小写、合并空格
const normalizeForSearch = (s: string): string => 
  stripDiacritics(s).toLowerCase().replace(/\s+/g, ' ').trim()

// 生成首字母缩写（排除常用词）
const getInitials = (name: string): string => {
  const exclude = [
    'für', 'und', 'der', 'die', 'das', 'von', 'zu', 'im', 'am', 'an',
    'of', 'the', 'and', 'for', 'universität', 'university', 
    'hochschule', 'fachhochschule', 'technik', 'technische'
  ]
  
  return stripDiacritics(name)
    .split(/[\s-]+/)
    .filter(w => w && !exclude.includes(w.toLowerCase()))
    .map(w => w[0])
    .join('')
    .toLowerCase()
}

// 检查是否匹配别名
const matchesAlias = (universityName: string, query: string): boolean => {
  const aliases = getUniversityAliases(universityName)
  const queryNorm = normalizeForSearch(query)
  
  return aliases.some(alias => 
    normalizeForSearch(alias).includes(queryNorm) || 
    queryNorm.includes(normalizeForSearch(alias))
  )
}

/**
 * 过滤大学列表（支持别名和首字母联想）
 * @param universities 大学列表
 * @param query 搜索关键词
 * @returns 过滤后的大学列表
 */
export function filterUniversities(universities: University[], query: string): University[] {
  if (!query.trim()) {
    return universities
  }

  const queryNorm = normalizeForSearch(query)
  const queryCompressed = queryNorm.replace(/\s+/g, '')
  
  return universities.filter((uni) => {
    const nameNorm = normalizeForSearch(uni.name)
    const cityNorm = normalizeForSearch(uni.city)
    const stateNorm = normalizeForSearch(uni.state)
    
    // 1. 直接匹配名称、城市、州
    if (nameNorm.includes(queryNorm) || 
        cityNorm.includes(queryNorm) || 
        stateNorm.includes(queryNorm)) {
      return true
    }
    
    // 2. 别名匹配（如 TUM, RWTH, KIT）
    if (matchesAlias(uni.name, queryNorm)) {
      return true
    }

    // 3. 首字母联想（如 "tu m" → Technische Universität München）
    const initials = getInitials(uni.name)
    if (initials.includes(queryCompressed)) {
      return true
    }
    
    return false
  })
}

/**
 * 排序搜索结果（精确匹配优先）
 * @param universities 大学列表
 * @param query 搜索关键词
 * @returns 排序后的大学列表
 */
export function sortUniversitiesByRelevance(universities: University[], query: string): University[] {
  if (!query.trim()) {
    return universities
  }

  const queryNorm = normalizeForSearch(query)
  const queryCompressed = queryNorm.replace(/\s+/g, '')

  return [...universities].sort((a, b) => {
    const aName = normalizeForSearch(a.name)
    const bName = normalizeForSearch(b.name)
    const aAliases = getUniversityAliases(a.name)
    const bAliases = getUniversityAliases(b.name)
    const aInitials = getInitials(a.name)
    const bInitials = getInitials(b.name)

    // 1. 名称精确开头匹配（最高优先级）
    const aStartsWith = aName.startsWith(queryNorm)
    const bStartsWith = bName.startsWith(queryNorm)
    if (aStartsWith && !bStartsWith) return -1
    if (!aStartsWith && bStartsWith) return 1
    
    // 2. 别名精确匹配
    const aExactAlias = aAliases.some(alias => normalizeForSearch(alias) === queryNorm)
    const bExactAlias = bAliases.some(alias => normalizeForSearch(alias) === queryNorm)
    if (aExactAlias && !bExactAlias) return -1
    if (!aExactAlias && bExactAlias) return 1

    // 3. 首字母精确匹配
    if (aInitials === queryCompressed && bInitials !== queryCompressed) return -1
    if (aInitials !== queryCompressed && bInitials === queryCompressed) return 1

    // 4. 按字母顺序
    return aName.localeCompare(bName)
  })
}

/**
 * 搜索并排序大学（组合函数）
 * @param universities 大学列表
 * @param query 搜索关键词
 * @returns 过滤并排序后的大学列表
 */
export function searchUniversities(universities: University[], query: string): University[] {
  const filtered = filterUniversities(universities, query)
  return sortUniversitiesByRelevance(filtered, query)
}
